"use client";
import React from "react";
import { ArrowBack } from "@mui/icons-material";
import { Box, Container, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRouter, usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";

const AppLogo =
  "https://getgeek-website-assets.b-cdn.net/ImageTerms%20and%20Privacy.webp";

const PageWrapper = styled(Box)({
  width: "100%",
  position: "relative",
  overflow: "auto",
  backgroundPosition: "center",
});

const ContentWrapper = styled(Container)({
  position: "relative",
  zIndex: 1,
  maxWidth: "900px",
  padding: "80px 20px",
  "@media (max-width: 768px)": { padding: "40px 16px" },
});

const BackButton = styled(IconButton)({
  width: "20px",
  height: "20px",
  border: "1.3px solid #8D8D8D",
  borderRadius: "50%",
  padding: "8px",
  color: "#8D8D8D",
  "&:hover": { backgroundColor: "rgba(141, 141, 141, 0.08)" },
  marginTop: "2%",
});

const MainTitle = styled(Typography)({
  fontFamily: "'Outfit', -apple-system, Roboto, Helvetica, sans-serif",
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: "120%",
  color: "#000",
  marginBottom: "10px",
  textTransform: "capitalize",
  "@media (max-width: 768px)": { fontSize: "24px" },
});

const Subtitle = styled(Typography)({
  fontFamily: "'Outfit', -apple-system, Roboto, Helvetica, sans-serif",
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "160%",
  letterSpacing: "0.36px",
  color: "rgba(0, 0, 0, 0.80)",
  marginBottom: "40px",
  "@media (max-width: 768px)": { fontSize: "14px" },
});

const ContentSection = styled(Box)({ marginBottom: "50px" });

const SectionTitle = styled(Typography)({
  fontFamily: "'Outfit', -apple-system, Roboto, Helvetica, sans-serif",
  fontSize: "20px",
  fontWeight: 600,
  color: "#000",
  marginBottom: "20px",
  "@media (max-width: 768px)": { fontSize: "18px" },
});

const BodyText = styled(Typography)({
  fontFamily: "'Outfit', -apple-system, Roboto, Helvetica, sans-serif",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "160%",
  letterSpacing: "0.36px",
  color: "rgba(0, 0, 0, 0.50)",
  marginBottom: "20px",
  "@media (max-width: 768px)": { fontSize: "13px" },
});

const MultilineText = ({ text, sx, fontWeight, color }) => (
  <BodyText sx={sx} fontWeight={fontWeight} color={color}>
    {text?.split("\n").map((line, i) => (
      <React.Fragment key={i}>
        {line}
        {i < text.split("\n").length - 1 && <br />}
      </React.Fragment>
    ))}
  </BodyText>
);

const TermsAndConditions = ({ onBack, type }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const pathType = pathname?.includes("/privacy") ? "privacy" : "terms";
  const finalType = type || pathType;
  const isPrivacyPolicy = finalType === "privacy";

  const handleBack = onBack || (() => router.back());
  const ns = isPrivacyPolicy ? "privacy" : "terms";

  return (
    <PageWrapper dir={isRTL ? "rtl" : "ltr"}>
      <Box
        sx={{
          position: "absolute",
          top: { xs: "20px", md: "30px" },
          left: isRTL ? "auto" : { xs: "20px", md: "30px" },
          right: isRTL ? { xs: "20px", md: "30px" } : "auto",
          zIndex: 10,
        }}
      >
        {/* <Box
          component="img"
          src={AppLogo}
          alt="Getgeek Logo"
          sx={{
            width: { xs: "120px", md: "165px" },
            height: "auto",
            objectFit: "contain",
          }}
        /> */}
      </Box>

      <ContentWrapper>
        <BackButton onClick={handleBack}>
          <ArrowBack
            sx={{
              fontSize: "12px",
              transform: isRTL ? "scaleX(-1)" : "none",
            }}
          />
        </BackButton>

        <Box mt={4}>
          <MainTitle component="h1">
            {isPrivacyPolicy
              ? t("termsAndConditions.privacyTitle")
              : t("termsAndConditions.termsTitle")}
          </MainTitle>
          <Subtitle>
            {isPrivacyPolicy
              ? t("termsAndConditions.privacyEffectiveDate")
              : t("termsAndConditions.termsEffectiveDate")}
          </Subtitle>

          {isPrivacyPolicy ? (
            <>
              {/* Intro */}
              <ContentSection>
                <BodyText>{t(`termsAndConditions.${ns}.intro`)}</BodyText>
              </ContentSection>

              {/* Section 1 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s1Title`)}
                </SectionTitle>

                {/* 1.1 Personal Information */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s1_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_1LegalBasisTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_1LegalBasisBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_1PurposeTitle`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_1PurposeText`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_1PurposeBullets`)}
                  sx={{ marginLeft: "20px" }}
                />

                {/* 1.2 Email Verification */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s1_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s1_2Footer`)}</BodyText>

                {/* 1.3 LinkedIn */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s1_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s1_3Footer`)}</BodyText>

                {/* 1.4 Google Workspace */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_4GoogleTitle`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_4GoogleText`)}
                </BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_4GoogleGmailTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_4GoogleGmailBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_4GoogleGmailFooter`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_4GoogleGmailMonitoring`)}
                </BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_4GoogleCalendarTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_4GoogleCalendarBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_4GoogleCalendarFooter`)}
                </BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_4GoogleControlTitle`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s1_4GoogleControlText`)}
                </BodyText>

                {/* 1.5 Candidate Data */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_5Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s1_4Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_4Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />

                {/* 1.6 Usage Data */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s1_6Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s1_5Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1_5Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>

              {/* Section 2 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s2Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s2Text`)}</BodyText>

                {/* 2.1 Core Services */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_1Title`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />

                {/* 2.2 AI Analysis */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s2_2Footer`)}</BodyText>

                {/* 2.3 AI Training */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  {t(`termsAndConditions.${ns}.s2_3Important`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s2_3GoogleException`)}
                </BodyText>

                {/* 2.4 Communication */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_4Title`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_4Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />

                {/* 2.5 Candidate Communication */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_5Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_5Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_5Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s2_5Footer`)}</BodyText>
              </ContentSection>

              {/* Google Compliance Section */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.sGoogleComplianceTitle`)}
                </SectionTitle>
                <BodyText>
                  {t(`termsAndConditions.${ns}.sGoogleComplianceText`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.sGoogleComplianceBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>
                  {t(`termsAndConditions.${ns}.sGoogleComplianceFooter`)}
                </BodyText>
              </ContentSection>

              {/* Section 3 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s3Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s3_1Footer`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_1FooterBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s3_2Footer`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>

              {/* Section 4 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s4Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s4Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_1Title`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s4_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_2Title`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s4_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s4_3Text`)}</BodyText>
              </ContentSection>

              {/* Section 5 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s5Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s5_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s5_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s5_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s5_2Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  {t(`termsAndConditions.${ns}.s5_2Important`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_2SubBullets`)}
                  sx={{ marginLeft: "40px" }}
                />
                <BodyText sx={{ marginLeft: "20px" }}>
                  {t(`termsAndConditions.${ns}.s5_2Footer1`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s5_2Footer2`)}</BodyText>
              </ContentSection>

              {/* Section 6 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s6Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s6_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s6_1Footer`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_2Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_2Email`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_2Footer`)}</BodyText>

                {/* 6.3 Google Account Permissions */}
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_3Text`)}</BodyText>
              </ContentSection>

              {/* Section 7 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s7Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>

              {/* Section 8 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s8Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s8Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s8Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s8Footer`)}</BodyText>
              </ContentSection>

              {/* Section 9 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s9Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s9Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s9Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s9NoticeTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s9NoticeBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s9Footer`)}</BodyText>
              </ContentSection>

              {/* Section 10 */}
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s10Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s10Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s10Contact`)}
                  sx={{ fontWeight: 600 }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s10ResponseTitle`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s10ResponseText`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s10Footer`)}</BodyText>
              </ContentSection>

              {/* Last Updated */}
              <ContentSection>
                <BodyText sx={{ fontWeight: 600, marginTop: "40px" }}>
                  {t(`termsAndConditions.${ns}.lastUpdated`)}
                </BodyText>
              </ContentSection>
            </>
          ) : (
            <>
              <ContentSection>
                <BodyText>{t(`termsAndConditions.${ns}.intro`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s1Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s2Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_1PurposeTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_1PurposeBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s2_1Legal`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s2_2Footer`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s2_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s2_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s2_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s2_3Footer`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s3Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s3_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s3_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s3_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s4Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s4_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s4_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s4_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s4_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s4_3Title`)}
                </BodyText>
                <BodyText sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  {t(`termsAndConditions.${ns}.s4_3Important`)}
                </BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s5Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s5_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s5_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s5_1Footer`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s5_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s5_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s5_2ProtectionTitle`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s5_2ProtectionBullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s6Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s6_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s6_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s6_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s6_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s6_2Footer`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s7Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_3Privacy`)}
                </BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s7_4Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s7_4Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s7_4Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s8Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s8_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s8_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s8_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s8_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s8_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s8_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s8_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s8_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s8_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s8_3Footer`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s9Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s9_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s9_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s9_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText>{t(`termsAndConditions.${ns}.s9_1Footer`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s10Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s10_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s10_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s10_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s10_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s10_2Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600, color: "#d32f2f" }}>
                  {t(`termsAndConditions.${ns}.s10_2Important`)}
                </BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s10_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s10_2SubBullets`)}
                  sx={{ marginLeft: "40px" }}
                />
                <BodyText sx={{ marginLeft: "20px" }}>
                  {t(`termsAndConditions.${ns}.s10_2Footer1`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s10_2Footer2`)}
                </BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s11Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s11_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s11_1Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s11_1Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s11_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s11_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s11_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s12Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s12_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s12_2Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s12_2Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s12_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s12_3Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s12_3Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s13Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s13Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s13Bullets`)}
                  sx={{ marginLeft: "20px" }}
                />
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s14Title`)}
                </SectionTitle>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s14_1Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s14_1Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s14_2Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s14_2Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s14_3Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s14_3Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s14_4Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s14_4Text`)}</BodyText>
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s14_5Title`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s14_5Text`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <SectionTitle>
                  {t(`termsAndConditions.${ns}.s15Title`)}
                </SectionTitle>
                <BodyText>{t(`termsAndConditions.${ns}.s15Text`)}</BodyText>
                <MultilineText
                  text={t(`termsAndConditions.${ns}.s15Contact`)}
                  sx={{ fontWeight: 600 }}
                />
                <BodyText sx={{ fontWeight: 600 }}>
                  {t(`termsAndConditions.${ns}.s15ResponseTitle`)}
                </BodyText>
                <BodyText>
                  {t(`termsAndConditions.${ns}.s15ResponseText`)}
                </BodyText>
                <BodyText>{t(`termsAndConditions.${ns}.s15Footer`)}</BodyText>
              </ContentSection>
              <ContentSection>
                <BodyText sx={{ fontWeight: 600, marginTop: "40px" }}>
                  {t(`termsAndConditions.${ns}.lastUpdated`)}
                </BodyText>
              </ContentSection>
            </>
          )}
        </Box>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default TermsAndConditions;
